import {
    RemoteJobCreationType,
    RemoteJobExpectedArgumentRepresentation,
    RemoteJobPredefinedActionRepresentation,
    RemoteJobRepresentation,
    RemoteJobStatus,
} from '@common/api';
import BorderedContainer from '@components/BorderedContainer';
import { Button } from '@components/button';
import { Modal } from '@components/modal/Modal';
import { ModalFooter } from '@components/modal/ModalFooter';
import { useModal } from '@components/modal/ModalProvider';
import { PageLayout } from '@components/page/PageLayout';
import { Tabs } from '@components/tabs/Tabs';
import { useRemoteJobActions, useRemoteJobById, useRemoteJobRunsByRemoteJobId } from '@data/hooks/remoteJobs';
import { useFolderEntityKey } from '@features/entity/entityFolder';
import {
    REMOTE_JOB_STATUS_COLOR_MAP,
    RemoteJobDescription,
    RemoteJobDetailsContent,
    RemoteJobHeader,
    RemoteJobIcon,
    RemoteJobScriptPill,
    RemoteJobStatusDot,
} from '@features/remoteJobs/RemoteJob.style';
import RemoteJobArgumentsForm from '@features/remoteJobs/RemoteJobArgumentsForm';
import { RemoteJobForm } from '@features/remoteJobs/RemoteJobForm';
import RemoteJobRuns, { RemoteJobRunUser } from '@features/remoteJobs/RemoteJobRuns';
import { useI18nContext } from '@i18n/i18n-react';
import { useAuth } from '@providers/AuthProvider';
import useTheme, { appColors } from '@base/useTheme';
import { PAGE_ICONS } from '@util/folderItems';
import { useState } from 'react';
import toast from 'react-hot-toast';
import { useNavigateWithParams } from '@hooks/useNavigateWithParams';
import { pagePath } from '@pages/pagePaths';
import { FolderBreadcrumbs } from '@components/folder-breadcrumbs/FolderBreadcrumbs';
import { ShareSidebarToggle } from '@features/sidebar/ShareSidebarToggle';
import { SidebarType } from '@util/enums';
import { ShareSidebar } from '@features/share-editor/ShareSidebar';
import { usePipelineVizEnabled } from '@features/labs/pipeline-visualization/usePipelineVizEnabled';

interface RunJobExpectedArgumentsModalProps {
    expectedArguments: RemoteJobExpectedArgumentRepresentation[];
    onConfirm: (expectedArguments: string[]) => void;
}

export function RunJobExpectedArgumentsModal({ expectedArguments, onConfirm }: RunJobExpectedArgumentsModalProps) {
    const { LL } = useI18nContext();
    const { hideModal } = useModal();
    const [jobArguments, setJobArguments] = useState<string[]>(expectedArguments.map((a) => a.defaultValue ?? ''));

    return (
        <Modal.Content>
            <Modal.Header title={LL.RUN_ARGUMENTS()} />
            <Modal.Body>
                <RemoteJobArgumentsForm
                    expectedArguments={expectedArguments}
                    jobArguments={jobArguments}
                    setJobArguments={setJobArguments}
                />
            </Modal.Body>
            <ModalFooter
                doneLabel={LL.RUN()}
                done={() => {
                    hideModal();
                    onConfirm(jobArguments);
                }}
            />
        </Modal.Content>
    );
}

interface RunJobButtonProps {
    remoteJob: RemoteJobRepresentation;
    onClick: () => void;
    disabled: boolean;
    action?: RemoteJobPredefinedActionRepresentation;
    hasFiles: boolean;
}

function RunJobButton({ remoteJob, onClick, disabled, action, hasFiles }: RunJobButtonProps) {
    const { LL } = useI18nContext();

    if (remoteJob.status === RemoteJobStatus.AVAILABLE) {
        if (remoteJob.creationType === RemoteJobCreationType.FARSEER) {
            if (!hasFiles) {
                return <Button title={LL.NO_FILES()} disabled />;
            } else if (remoteJob.mainScriptFileId === undefined) {
                return <Button title={LL.NO_MAIN_FILE()} disabled />;
            } else {
                return <Button title={action?.name ?? LL.RUN_JOB()} onClick={onClick} disabled={disabled} />;
            }
        } else {
            return <Button title={LL.RUN_JOB()} onClick={onClick} disabled={disabled} />;
        }
    } else if (remoteJob.status === RemoteJobStatus.QUEUED) {
        return <Button title={LL.QUEUED()} disabled />;
    } else if (remoteJob.status === RemoteJobStatus.RUNNING) {
        return <Button title={LL.RUNNING()} disabled />;
    } else {
        return <Button title={LL.UNAVAILABLE()} disabled />;
    }
}

export function areActionArgumentsValid(
    expectedArguments: RemoteJobExpectedArgumentRepresentation[],
    runArguments: { [key: string]: string }
): boolean {
    const runArgumentsKeys = Object.keys(runArguments);
    if (expectedArguments.length !== runArgumentsKeys.length) {
        return false;
    }
    for (const expectedArgument of expectedArguments) {
        if (!runArguments[expectedArgument.name]) {
            return false;
        }
    }
    return true;
}

export default function RemoteJobDetails() {
    const { LL } = useI18nContext();

    const jobEntityKey = useFolderEntityKey();

    const remoteJobId = jobEntityKey.id;
    const remoteJob = useRemoteJobById(remoteJobId);
    const remoteJobRuns = useRemoteJobRunsByRemoteJobId(remoteJobId);
    const { runRemoteJob } = useRemoteJobActions();
    const lastJobRun = remoteJobRuns[0];
    const { showModal } = useModal();
    const theme = useTheme();
    const { user } = useAuth();
    const navigate = useNavigateWithParams();
    const remoteJobClassActions = user?.classActions.remoteJobClassActions;
    const [pipelineVizEnabled] = usePipelineVizEnabled();
    const showPipelineButton = pipelineVizEnabled && !!user?.features.canSeeFarseerLabs;

    // Use pre-resolved script files from the API response (no per-item permission filtering)
    const folderFiles = remoteJob?.scriptFiles ?? [];

    const TABS = [LL.GENERAL(), LL.SETTINGS()] as const;
    type Tab = (typeof TABS)[number];

    const [selectedTab, setSelectedTab] = useState<Tab>(TABS[0]);

    if (!remoteJob) {
        return <></>;
    }

    function onRunClick(action?: RemoteJobPredefinedActionRepresentation) {
        if (remoteJob?.expectedArguments && remoteJob.expectedArguments.length > 0) {
            if (action && action.runArguments) {
                if (areActionArgumentsValid(remoteJob.expectedArguments, action.runArguments)) {
                    runRemoteJob({
                        remoteJobId: remoteJobId,
                        jobArguments: remoteJob.expectedArguments.map((arg) => action.runArguments[arg.name]),
                    });
                } else {
                    toast.error(LL.RUN_ARGUMENTS_ERROR());
                }
            } else {
                showModal(
                    <RunJobExpectedArgumentsModal
                        expectedArguments={remoteJob.expectedArguments}
                        onConfirm={(jobArguments) =>
                            runRemoteJob({ remoteJobId: remoteJobId, jobArguments: jobArguments })
                        }
                    />
                );
            }
        } else {
            runRemoteJob({ remoteJobId: remoteJobId, jobArguments: [] });
        }
    }

    const folderBreadcrumbs = <FolderBreadcrumbs entityKey={jobEntityKey} pathType={pagePath.Folder} />;

    const title = remoteJob.name;

    return (
        <PageLayout
            toggles={<ShareSidebarToggle />}
            sidebars={{ [SidebarType.SHARE]: <ShareSidebar /> }}
            title={title}
            folderBreadcrumbs={folderBreadcrumbs}
            icon={PAGE_ICONS.App}
            padded
        >
            <RemoteJobDetailsContent>
                <RemoteJobHeader>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <RemoteJobIcon icon={remoteJob.icon} name={remoteJob.name} />
                        <h3 style={{ marginLeft: '15px' }}>{remoteJob.name}</h3>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: theme.space.m }}>
                        {showPipelineButton && (
                            <Button
                                $type="secondary"
                                title="Pipeline Visualization"
                                onClick={() =>
                                    navigate(
                                        `${pagePath.Labs}?tab=data-platform&view=pipelines&remoteJobId=${remoteJobId}`
                                    )
                                }
                            />
                        )}
                        {remoteJob.predefinedActions?.map((action) => (
                            <RunJobButton
                                key={action.name}
                                action={action}
                                remoteJob={remoteJob}
                                onClick={() => onRunClick(action)}
                                disabled={!(remoteJobClassActions?.canRun ?? false)}
                                hasFiles={remoteJob.mainScriptFileId !== undefined}
                            />
                        ))}
                        <RunJobButton
                            remoteJob={remoteJob}
                            onClick={onRunClick}
                            disabled={!(remoteJobClassActions?.canRun ?? false)}
                            hasFiles={remoteJob.mainScriptFileId !== undefined}
                        />
                    </div>
                </RemoteJobHeader>
                <Tabs tabs={TABS} selected={selectedTab} onSelect={(tab: Tab) => setSelectedTab(tab)} />
                {selectedTab === LL.GENERAL() && (
                    <>
                        <h4>{LL.BASIC_INFO()}</h4>
                        <BorderedContainer
                            style={{ borderColor: appColors.neutral.w200, padding: '10px', flexGrow: 0 }}
                        >
                            <h4 style={{ marginTop: 0 }}>{LL.DESCRIPTION()}</h4>
                            <RemoteJobDescription>{remoteJob.description}</RemoteJobDescription>
                            <h4>{LL.LAST_RUN()}</h4>
                            {lastJobRun ? (
                                <RemoteJobDescription>
                                    <RemoteJobStatusDot $color={REMOTE_JOB_STATUS_COLOR_MAP[lastJobRun.status].text} />
                                    {lastJobRun.status}
                                    &nbsp;-&nbsp;
                                    <RemoteJobRunUser remoteJobRun={lastJobRun} />
                                    &nbsp;@&nbsp;
                                    {lastJobRun.startedAt.toLocaleString('hr-HR')}
                                </RemoteJobDescription>
                            ) : (
                                <p>{LL.NEVER_RUN()}</p>
                            )}
                            <h4>{LL.FILES()}</h4>
                            <div style={{ display: 'flex', gap: theme.space.s, flexWrap: 'wrap' }}>
                                {folderFiles.map((file) => (
                                    <RemoteJobScriptPill
                                        key={file.id}
                                        role="button"
                                        $selected={file.id === remoteJob.mainScriptFileId}
                                    >
                                        {file.name}
                                    </RemoteJobScriptPill>
                                ))}
                            </div>
                        </BorderedContainer>
                        <h4>{LL.LOGS()}</h4>
                        <BorderedContainer style={{ borderColor: appColors.neutral.w200, flexGrow: 1 }}>
                            <RemoteJobRuns remoteJobRuns={remoteJobRuns} />
                        </BorderedContainer>
                    </>
                )}
                {selectedTab === LL.SETTINGS() && <RemoteJobForm remoteJob={remoteJob} />}
            </RemoteJobDetailsContent>
        </PageLayout>
    );
}
